import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, switchMap, tap } from 'rxjs/operators';

import { TabsComponent } from '../tabs/tabs.component';
import { ngxLoadingAnimationTypes } from 'ngx-loading';
import * as _ from 'lodash';
import { dateAsString, dateTimeAsString } from '../../date.helper';
import { UserService } from '../../../user/user.service';

const PrimaryWhite = '#ffffff';
const SecondaryGrey = '#ccc';

@Component({
  selector: 'app-list-view',
  templateUrl: './list-view.component.html',
  styleUrls: ['./list-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListViewComponent implements OnInit, OnDestroy, AfterViewInit {
  type = 'list';
  page;
  pageSize;
  searchString;
  currentFilters;
  preservefilters;
  private _orderBy;
  private _asc = true;
  private subscriptions: Subscription = new Subscription();
  private loadDebounced = new Subject<void>();
  public ngxLoadingAnimationTypes = ngxLoadingAnimationTypes;
  public primaryColour = PrimaryWhite;
  public secondaryColour = SecondaryGrey;

  @ViewChild('tabs', { static: true }) tabs: TabsComponent;

  get orderBy() {
    return this._orderBy;
  }
  set orderBy(value: string) {
    this._orderBy = value;
  }
  get asc() {
    return this._asc;
  }
  set asc(value: boolean) {
    this._asc = value;
  }

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    public userService: UserService,
  ) {}

  @Input() hideTabs = false;
  @Input() count = 0;
  @Input() data = [];
  @Input() column;
  @Input() canEdit;
  @Input() url;
  @Input() title;
  @Input() loading = false;
  @Input() actions: any;
  @Input() canSimpleSearch = true;
  @Input() canOrder = true;
  @Input() showAction = true;
  @Input() tabsName: any = { active: 'Active', archived: 'Archived' };
  @Input() SearchPlaceholder: 'Search';
  @Input() setInspectionSearch: boolean = false;
  @Input() tabType;
  @Input() CreateUrl;
  @Input() defaultOrderBy;
  @Input() defaultAsc = true;
  @Input() definedFilters;
  @Input() canAdvanceSearch = false;
  @Input() parms = {};
  @Input() tableName = '';

  @Output() action = new EventEmitter<void>();
  @Output() filters = new EventEmitter<any>();
  @Output() onPageChangedevent = new EventEmitter<void>();

  async ngOnInit() {
    this.preservefilters = await this.userService.getSetting(this.tableName);
    if (this.preservefilters) await this.applyPreserveFilters();
    this.subscriptions.add(
      this.route.queryParams.subscribe(async params => {
        await this.updateParams(params);
        //to presit the filters
        if (this.tableName && params && Object.keys(params).length > 0) {
          await this.userService.saveSetting(this.tableName, params).toPromise();
        }
        if (params && Object.keys(params).length > 0) await this.updateParams(params);
      }),
    );
    if (!this.CreateUrl) {
      this.CreateUrl = '/' + this.url + '/form/add';
    }
  }

  async updateParams(params: any) {
    this.page = Number(params['page']) || 1;
    this.pageSize = Number(params['pageSize']) || 10;
    this.searchString = params['search'] || '';
    this.asc = params['asc'] ? params['asc'] === 'true' : this.defaultAsc;
    this.orderBy = params['orderBy'] || this.defaultOrderBy || this.column[0].orderBy;
    this.type = params['type'] || 'list';
    this.parms = params;
    this.filters.emit(this.getQueryParams());
  }

  applyPreserveFilters() {
    if (this.preservefilters) {
      // this.page = Number(this.preservefilters['page']) || 1;
      this.pageSize = Number(this.preservefilters['pageSize']) || 10;
      this.searchString = this.preservefilters['search'] || '';
      this.asc = this.preservefilters['asc'] ? this.preservefilters['asc'] === 'true' : this.defaultAsc;
      this.orderBy = this.preservefilters['orderBy'] || this.defaultOrderBy || this.column[0].orderBy;
      this.type = this.preservefilters['type'] || 'list';
      this.parms = this.preservefilters;
      this.setParams();
      console.log('this.getQueryParams()', this.getQueryParams());
      this.filters.emit(this.getQueryParams());
    }
  }

  getQueryParams() {
    return {
      page: this.page,
      pageSize: this.pageSize,
      search: this.searchString,
      orderBy: this.orderBy,
      asc: this.asc,
      type: this.type,
      ...this.currentFilters,
    };
  }

  async setParams() {
    this.router.navigate([], {
      queryParams: this.getQueryParams(),
    });

    try {
      console.log('this.hideTabs', this.hideTabs);
      if (!this.hideTabs) {
        await this.tabs.refreshCount();
      }
    } catch (error) {
      console.error('Error refreshing tabs count:', error);
    }

    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    this.subscriptions.add(
      this.loadDebounced
        .pipe(
          tap(),
          debounceTime(1000),
          tap(),
          switchMap(async () => {
            this.router.navigate([], {
              queryParams: {
                page: this.page,
                pageSize: this.pageSize,
                search: this.searchString,
                orderBy: this.orderBy,
                asc: this.asc,
                type: this.type,
              },
            });
          }),
          catchError(error => {
            console.error('Error in stream:', error);
            return EMPTY;
          }),
        )
        .subscribe(),
    );
  }

  async setSearch() {
    this.loadDebounced.next();
  }

  setSearchTerm(search) {
    this.searchString = search;
    this.loadDebounced.next();
  }

  getRowData(item: any, field: string): any {
    return _.get(item, field, null);
  }

  async onPageChanged(page) {
    this.page = page;
    this.setParams();
  }

  setOrder(orderBy) {
    if (!orderBy) {
      return;
    }
    this.asc = this.orderBy === orderBy ? !this.asc : true;
    this.orderBy = orderBy;
    this.page = 1;
    this.setParams();
  }

  getOrderIcon(orderBy: string) {
    return {
      'fa-arrow-down': this.orderBy == orderBy && this.asc,
      'fa-arrow-up': this.orderBy == orderBy && !this.asc,
    };
  }

  open(ev, id: number) {
    if (this.url) {
      this.router.navigateByUrl(this.url + `/form/${id}`);
    }
  }

  // async executeFieldAction(action, event, item) {
  //   console.log('executeAction', action, item);
  //   await action.callback(event, item)();
  //   this.filters.emit(this.getQueryParams());
  //   this.tabs.refreshCount();
  // }

  async executeAction(action, item) {
    console.log('executeAction', action, item);
    await action.callback(item)();
    this.filters.emit(this.getQueryParams());
    this.tabs.refreshCount();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  getLink(item: any, column: any): string {
    let link = column.link;
    const matches = link.match(/{{\s*(.+?)\s*}}/g);

    if (matches) {
      matches.forEach(match => {
        const key = match.replace(/[{}]/g, '').trim();
        const value = this.getRowData(item, key);
        link = link.replace(match, value);
      });
    }
    return link;
  }

  getDateAndTime(date: Date) {
    return dateTimeAsString(date);
  }

  dateAsString(date: Date) {
    return dateAsString(date);
  }

  async filtersUpdated(filters: any) {
    console.log(filters);
    this.loading = true;
    this.currentFilters = {};

    for (const filter of filters.filter(f => f.key !== null)) {
      this.currentFilters[filter.key] = filter.value;
      if (filter.operator) {
        this.currentFilters[filter.key + 'Operator'] = filter.operator;
      }
    }
    this.filters.emit({ ...this.getQueryParams(), ...this.currentFilters });
  }
}
